import React, { useMemo, useState, useEffect } from 'react';
import { useIntl } from 'umi';
import classnames from 'classnames';
import { Button, Transfer, Modal } from 'tea-component';
import Loading from '@/pages/intelligence/components/Loading';
import FirstRow from '@/pages/intelligence/components/TableComponents/components/FirstRow';
import { getCountryFlag } from '@/pages/home/_config/countries';
import KeywordsSelect from './components/KeywordSelect';
import SourceTable from './components/SourceTable';
import TargetTable from './components/TargetTable';
import { getEntitiesKeys } from './utils';
import { getCountryCodeByCountryName } from '@/pages/intelligence/utils/commonUtils';
import { getDetailGameOverviewPath } from '@/pages/intelligence/utils/topchartsUtils';
import { GameSegmentType } from '../../types';
import { GameSegmentOptions } from '../../_config/constant';

import type { IEntity, IAddEntitiesProps } from './types';

import './index.scss';
import TipContext from '@/pages/home/components/TipContext';

export default function AddEntities(props: IAddEntitiesProps) {
  const { formatMessage } = useIntl();

  const {
    entityKey,
    title = formatMessage({ id: 'dbi_compare_add_game_to_compare' }),
    leftCellTitle = formatMessage({ id: 'dbi_compare_similar_games' }),
    rightCellTitle = formatMessage({ id: 'dbi_games' }),
    searchPlaceholder = formatMessage({ id: 'dbi_search_any_game' }),
    noSearchResultTips = formatMessage({ id: 'dbi_no_search_results' }),
    pleaseSelectTips = formatMessage({ id: 'dbi_please_a_select_game' }),
    notSelectTips = (
      <>
        {formatMessage({ id: 'dbi_compare_not_select_game_yet' })}
        <br />
        {formatMessage({ id: 'dbi_compare_please_search_game' })}
      </>
    ),
    entities,
    visible,
    entityRenderer: entityRendererOutter,
    onConfirm,
    onClose,

    keywordsFetcher,
    similarEntitiesFetcher,
    entitiesFetcher,
    tipText,
    entityType,
    followedGameList,
    regionsCountries,
    getSearchNewList,
    gameSegmentList,
    customGameList,
    customLoading,
    allGameList,
  } = props;

  function DefaultEntityRenderer(item: any) {
    return (
      <FirstRow
        cvm={item?.cvm}
        rihgtClickToJumpHref={getDetailGameOverviewPath(
          item?.cvm?.gameId,
          entityType,
        )}
      />
    );
  }

  const gameOptions = useMemo(() => {
    return GameSegmentOptions?.filter(
      (item) => item.value !== 'customize_mobile',
    );
  }, [GameSegmentOptions]);

  const entityRenderer =
    typeof entityRendererOutter === 'function'
      ? entityRendererOutter
      : DefaultEntityRenderer;

  const entityRendererWithKeyword = (tablePayload: any) =>
    entityRenderer(tablePayload, keyword);

  const [isLoading, setIsLoading] = useState(false);
  // 实体搜索关键字，这个关键字不是用户直接输入的，而是根据用户输入的关键字 在后台查询后 用户再次点击选择的关键字（用来搜索游戏 匹配概率更高？）
  const [keyword, setKeyword] = useState('');

  const [gameActive, setGameActive] = useState<
    {
      [key in GameSegmentType]?: boolean;
    }
  >({
    gacha: false,
    slg: false,
    casual: false,
    mmo: false,
    shooter: false,
    sports: false,
    top: false,
  });

  // 实体搜索关键字change
  const handleKeywordChange = async ({ id, keyword }) => {
    setKeyword(keyword);
    if (!keyword?.trim()) return;
    try {
      setIsLoading(true);

      let entitiesTemp: any = await entitiesFetcher?.({
        entity_type: entityType,
        unified_id: id ? [id] : undefined,
        search_by_name: keyword,
      });

      entitiesTemp = Array.isArray(entitiesTemp) ? entitiesTemp : [];

      const searchResult = entitiesTemp?.find(
        (item) => item?.genre_type === 'search',
      );

      if (searchResult) {
        const resultList = searchResult?.list || [];
        const list = resultList?.map((item) => ({
          gameId: item.id,
          gametitle: item.entity_name,
          text: item.company,
          img: item.cover,
          countryIcon: getCountryFlag(item.company_country),
        }));
        setSearchEntities(list);
      } else {
        setSearchEntities([]);
      }
    } catch (exception) {
    } finally {
      setIsLoading(false);
    }
  };

  // similar实体数据
  const [similarEntities, setSimilarEntities] = useState<IEntity[]>([]);
  // 搜索出来的实体数据
  const [searchEntities, setSearchEntities] = useState<IEntity[]>([]);
  // 勾选的实体数据
  const [checkedEntities, setCheckedEntities] = useState<IEntity[]>([]);
  // 勾选的实体数据的keys
  const checkedEntitiesKeys = useMemo<string[]>(
    () => getEntitiesKeys(checkedEntities, entityKey),
    [entityKey, checkedEntities],
  );
  const handleCheckedEntitiesKeysChange = (payload: string[]) => {
    const checkedEntitiesTemp: IEntity[] = [];
    const similarEntitiesKeysMap: {
      [key: string]: boolean;
    } = {};
    const similarEntitiesAndSearchEntitiesTemp = [
      ...checkedEntities,
      ...similarEntities,
      ...searchEntities,
    ];
    for (const entity of similarEntitiesAndSearchEntitiesTemp) {
      const entityKeyTemp = entity?.[entityKey];
      if (!payload?.includes(entityKeyTemp)) continue;
      if (similarEntitiesKeysMap[entityKeyTemp]) continue;
      checkedEntitiesTemp.unshift(entity);
      similarEntitiesKeysMap[entityKeyTemp] = true;
    }
    setCheckedEntities(checkedEntitiesTemp);
  };
  // const isDisabled = useMemo<boolean>(
  //   () => checkedEntitiesKeys?.length >= max,
  //   [checkedEntitiesKeys?.length, max],
  // );

  const allGameIdConfig = useMemo(() => {
    const allGameIdConfig = allGameList?.reduce((result, item) => {
      const list = item?.list || [];
      result[item.genre_type] = list?.map((item) => item.id);
      return result;
    }, {});
    return allGameIdConfig;
  }, [allGameList]);

  useEffect(() => {
    const allGameIdConfig = allGameList?.reduce((result, item) => {
      const list = item?.list || [];
      result[item.genre_type] = list?.map((item) => item.id);
      return result;
    }, {});
    let result: {
      [key in GameSegmentType]?: boolean;
    } = {};

    gameOptions?.forEach((item) => {
      if (
        allGameIdConfig?.[item?.value]?.every((config) =>
          checkedEntitiesKeys?.includes(config),
        )
      ) {
        result[item?.value as GameSegmentType] = true;
      } else {
        result[item?.value as GameSegmentType] = false;
      }
    });
    setGameActive(result);
  }, [allGameList, visible]);

  const handleRemoveCheckedEntity = (payload: string) => {
    const checkedEntitiesTemp: IEntity[] =
      checkedEntities?.filter((item) => item?.[entityKey] !== payload) || [];
    setCheckedEntities(checkedEntitiesTemp);
    gameOptions?.forEach((item) => {
      if (allGameIdConfig?.[item?.value]?.includes(payload)) {
        setGameActive({
          ...gameActive,
          [item?.value]: false,
        });
      }
    });
  };

  const handleClearAll = () => {
    setGameActive({
      gacha: false,
      slg: false,
      casual: false,
      mmo: false,
      shooter: false,
      sports: false,
      top: false,
    });
    setCheckedEntities([]);
  };

  const handleConfirm = (e?: React.MouseEvent<Element, MouseEvent>) => {
    e?.stopPropagation();
    e?.preventDefault();
    onConfirm?.(checkedEntitiesKeys, checkedEntities);
  };

  const handleCancel = (e?: React.MouseEvent<Element, MouseEvent>) => {
    e?.stopPropagation();
    e?.preventDefault();
    onClose?.();
  };

  const initData = async () => {
    // if (!Array.isArray(entities)) return;
    setIsLoading(true);
    // setCheckedEntities(entities);
    try {
      let similarEntitiesTemp = await similarEntitiesFetcher?.(entities);

      const gameData = Array.isArray(similarEntitiesTemp?.list)
        ? similarEntitiesTemp?.list
        : [];

      const list = gameData?.map((item) => ({
        gameId: item.id,
        gametitle: item.entity_name,
        text: item.company,
        img: item.cover,
        countryIcon: getCountryFlag(item.company_country),
      }));
      setSimilarEntities(list);
    } catch (exception) {
      setSimilarEntities([]);
    } finally {
      setIsLoading(false);
    }
  };

  const resetData = () => {
    setKeyword('');
    setSearchEntities([]);
    setSimilarEntities([]);
    setCheckedEntities([]);
    setIsLoading(false);
  };

  useEffect(() => {
    if (visible) {
      initData();
    } else {
      resetData();
    }
  }, [visible]);

  const renderSearchedGames = () => {
    if (!searchEntities?.length)
      return (
        <div className="dbi-table-no-data dbi-left-cell">
          <div>
            <div style={{ textAlign: 'center' }}>{noSearchResultTips}</div>
            <div>
              {formatMessage(
                { id: 'dbi_type_games_only' },
                { type: formatMessage({ id: `dbi_${entityType}_games` }) },
              )}
            </div>
          </div>
        </div>
      );
    return (
      <SourceTable
        title={leftCellTitle}
        // isDisabled={isDisabled}
        // disabledMsg={limitMsg}
        hideHeader={true}
        entityKey={entityKey}
        records={searchEntities}
        value={checkedEntitiesKeys}
        onChange={handleCheckedEntitiesKeysChange}
        entityRenderer={entityRendererWithKeyword}
      />
    );
  };

  const renderSimilarGames = () => {
    if (!similarEntities?.length)
      return (
        <div className="dbi-table-no-data dbi-left-cell">{notSelectTips}</div>
      );
    return (
      <SourceTable
        title={leftCellTitle}
        // isDisabled={isDisabled}
        // disabledMsg={limitMsg}
        entityKey={entityKey}
        records={similarEntities}
        value={checkedEntitiesKeys}
        onChange={handleCheckedEntitiesKeysChange}
        entityRenderer={entityRenderer}
      />
    );
  };

  const renderLeftCell = () => {
    if (isLoading) return <Loading />;
    if (keyword) return renderSearchedGames();
    return renderSimilarGames();
  };

  const renderRightCell = () => (
    <TargetTable
      className={classnames({ 'is-no-data': !checkedEntities?.length })}
      title={
        <>
          {rightCellTitle}
          {gameOptions?.length > 0 ? (
            <div className="selected-header-shortcut">
              {gameOptions?.map((item) => (
                <span
                  className={classnames([
                    'shortcut-tag',
                    {
                      active:
                        gameActive?.[item?.value as GameSegmentType] || false,
                    },
                    {
                      disabled: customLoading,
                    },
                  ])}
                  onClick={() =>
                    handleShortcutClick(item?.value as GameSegmentType)
                  }
                >
                  {formatMessage({ id: item?.text })}
                </span>
              ))}
            </div>
          ) : null}
        </>
      }
      pleaseSelectTips={pleaseSelectTips}
      entityKey={entityKey}
      records={checkedEntities}
      onRemove={handleRemoveCheckedEntity}
      entityRenderer={entityRenderer}
    />
  );

  useEffect(() => {
    if (followedGameList) {
      setCheckedEntities(followedGameList);
    }
  }, [followedGameList, visible]);

  const isDisabled = useMemo(() => {
    if (followedGameList) {
      if (followedGameList.length !== checkedEntitiesKeys?.length) {
        return false;
      } else {
        // 右侧列表全为已关注过的游戏
        return followedGameList?.every((item) =>
          checkedEntitiesKeys.includes(item.gameId),
        );
      }
    } else {
      return !checkedEntitiesKeys?.length;
    }
  }, [followedGameList, visible, checkedEntitiesKeys]);

  const handleShortcutClick = async (type: GameSegmentType) => {
    if (customLoading) return;
    const targetGenreType = allGameList?.find(
      (item) => item.genre_type === type,
    );

    if (targetGenreType) {
      const gameList = targetGenreType?.list || [];
      const gameIdList = gameList?.map((item) => item?.id);
      const filterCheckedEntities = checkedEntities?.filter(
        (item) => !gameIdList?.includes(item.gameId),
      );
      if (gameActive[type]) {
        setCheckedEntities(filterCheckedEntities);
      } else {
        const list = gameList?.map((item) => ({
          gameId: item.id,
          gametitle: item.entity_name,
          text: item.company,
          img: item.cover,
          countryIcon: getCountryFlag(item.company_country),
        }));
        setCheckedEntities([...list, ...filterCheckedEntities]);
      }
    }

    setGameActive({
      ...gameActive,
      [type]: !gameActive[type],
    });
  };

  return (
    <Modal
      className="dbi-add-entities-modal dbi-add-entities-follow"
      visible={visible}
      caption={
        <div className="dbi-add-title">
          {title}
          {tipText && (
            <TipContext className="dbi-add-title-icon" tipText={tipText} />
          )}
        </div>
      }
      size={800}
      onClose={() => onClose?.()}
    >
      <Modal.Body>
        <Transfer
          // selection-centered 组件库使用的 公共样式
          className="dbi-add-games-compare-mobile-benchmark selection-centered"
          leftCell={
            <Transfer.Cell
              scrollable={false}
              title={formatMessage({ id: 'dbi_game_add' })}
              header={
                <KeywordsSelect
                  searchPlaceholder={searchPlaceholder}
                  keywordsFetcher={keywordsFetcher}
                  value={keyword}
                  entityType={entityType}
                  onChange={handleKeywordChange}
                />
              }
            >
              {renderLeftCell()}
            </Transfer.Cell>
          }
          rightCell={
            <Transfer.Cell
              className="dbi-compare-right"
              scrollable={false}
              title={
                <div className="compare-title-flex">
                  <div className="compare-title">
                    {formatMessage({ id: 'dbi_compare_selected' })} (
                    {checkedEntitiesKeys?.length})
                  </div>

                  {checkedEntities?.length > 0 && (
                    <Button
                      type="link"
                      className="compare-btn"
                      onClick={handleClearAll}
                    >
                      {formatMessage({ id: 'dbi_compare_clear_all' })}
                    </Button>
                  )}
                </div>
              }
            >
              {renderRightCell()}
            </Transfer.Cell>
          }
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          onClick={handleConfirm}
          disabled={isDisabled || !checkedEntitiesKeys?.length}
        >
          {formatMessage({ id: 'dbi_confirm' })}
        </Button>
        <Button type="weak" onClick={handleCancel}>
          {formatMessage({ id: 'dbi_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export type { IAddEntitiesProps };
